import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { isGameMaster } from '../lib/utils';
import { MainLayout } from '../components/layout/MainLayout';
import {
  NarrativeNodeGraph,
  NarrativeNodeDetailsPanel,
  NarrativeNodeEditor,
  NarrativeEdgeEditor,
} from '../components/narrative';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Loader2, GitBranch, Plus, Sparkles, Target } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';

const API_URL = '/api';

export default function NarrativeTimelinePage() {
  const { campaignId } = useParams();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  const lang = language;

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  // Data state
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [nodeEditorOpen, setNodeEditorOpen] = useState(false);
  const [edgeEditorOpen, setEdgeEditorOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [editingEdge, setEditingEdge] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Create quest from node dialog
  const [questFromNodeOpen, setQuestFromNodeOpen] = useState(false);
  const [questFromNodeSource, setQuestFromNodeSource] = useState(null);
  const [questFromNodeForm, setQuestFromNodeForm] = useState({ title: '', quest_type: 'side', summary: '' });
  const [creatingQuest, setCreatingQuest] = useState(false);

  const selectedNode = nodes.find(n => n.id === selectedNodeId);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [nodesRes, edgesRes, npcsRes, locsRes, questsRes] = await Promise.all([
        axios.get(`${API_URL}/campaigns/${campaignId}/narrative/nodes`),
        axios.get(`${API_URL}/campaigns/${campaignId}/narrative/edges`),
        axios.get(`${API_URL}/campaigns/${campaignId}/npcs`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/campaigns/${campaignId}/locations`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/campaigns/${campaignId}/quests`).catch(() => ({ data: [] })),
      ]);
      setNodes(nodesRes.data);
      setEdges(edgesRes.data);
      setNpcs(npcsRes.data);
      setLocations(locsRes.data);
      setQuests(questsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur de chargement' : 'Loading error'));
    } finally {
      setLoading(false);
    }
  }, [campaignId, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- Node CRUD ---
  const handleAddNode = () => {
    setEditingNode(null);
    setNodeEditorOpen(true);
  };

  const handleEditNode = (node) => {
    setEditingNode(node);
    setNodeEditorOpen(true);
  };

  const handleSaveNode = async (formData) => {
    setSaving(true);
    try {
      if (editingNode?.id) {
        const res = await axios.patch(`${API_URL}/narrative/nodes/${editingNode.id}`, formData);
        setNodes(prev => prev.map(n => n.id === editingNode.id ? res.data : n));
        toast.success(lang === 'fr' ? 'Noeud modifié' : 'Node updated');
      } else {
        const res = await axios.post(`${API_URL}/campaigns/${campaignId}/narrative/nodes`, formData);
        setNodes(prev => [...prev, res.data]);
        toast.success(lang === 'fr' ? 'Noeud créé' : 'Node created');
        setSelectedNodeId(res.data.id);
      }
      setNodeEditorOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateNode = async (nodeId) => {
    try {
      const res = await axios.post(`${API_URL}/narrative/nodes/${nodeId}/duplicate`);
      setNodes(prev => [...prev, res.data]);
      setSelectedNodeId(res.data.id);
      toast.success(lang === 'fr' ? 'Noeud dupliqué' : 'Node duplicated');
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  const handleDeleteNode = async (nodeId) => {
    setDeleteConfirm({ type: 'node', id: nodeId });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'node') {
        await axios.delete(`${API_URL}/narrative/nodes/${deleteConfirm.id}`);
        setNodes(prev => prev.filter(n => n.id !== deleteConfirm.id));
        setEdges(prev => prev.filter(e => e.source_id !== deleteConfirm.id && e.target_id !== deleteConfirm.id));
        if (selectedNodeId === deleteConfirm.id) setSelectedNodeId(null);
        toast.success(lang === 'fr' ? 'Noeud supprimé' : 'Node deleted');
      } else if (deleteConfirm.type === 'edge') {
        await axios.delete(`${API_URL}/narrative/edges/${deleteConfirm.id}`);
        setEdges(prev => prev.filter(e => e.id !== deleteConfirm.id));
        toast.success(lang === 'fr' ? 'Connexion supprimée' : 'Connection deleted');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleStatusChange = async (nodeId, newStatus) => {
    try {
      const res = await axios.patch(`${API_URL}/narrative/nodes/${nodeId}`, { status: newStatus });
      setNodes(prev => prev.map(n => n.id === nodeId ? res.data : n));
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  // --- Edge CRUD ---
  const handleConnect = (sourceId, targetId) => {
    setEditingEdge({ source_id: sourceId, target_id: targetId });
    setEdgeEditorOpen(true);
  };

  const handleEdgeClick = (edgeId) => {
    const edge = edges.find(e => e.id === edgeId);
    if (edge) {
      setEditingEdge(edge);
      setEdgeEditorOpen(true);
    }
  };

  const handleSaveEdge = async (formData) => {
    setSaving(true);
    try {
      if (editingEdge?.id) {
        const res = await axios.patch(`${API_URL}/narrative/edges/${editingEdge.id}`, formData);
        setEdges(prev => prev.map(e => e.id === editingEdge.id ? res.data : e));
        toast.success(lang === 'fr' ? 'Connexion modifiée' : 'Connection updated');
      } else {
        const res = await axios.post(`${API_URL}/campaigns/${campaignId}/narrative/edges`, formData);
        setEdges(prev => [...prev, res.data]);
        toast.success(lang === 'fr' ? 'Connexion créée' : 'Connection created');
      }
      setEdgeEditorOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEdge = (edgeId) => {
    setEdgeEditorOpen(false);
    setDeleteConfirm({ type: 'edge', id: edgeId });
  };

  // --- Positions bulk save ---
  const handlePositionsSave = useCallback(async (positions) => {
    try {
      await axios.patch(`${API_URL}/campaigns/${campaignId}/narrative/nodes/positions`, positions);
      setNodes(prev => {
        const posMap = Object.fromEntries(positions.map(p => [p.id, p]));
        return prev.map(n => posMap[n.id] ? { ...n, position_x: posMap[n.id].x, position_y: posMap[n.id].y } : n);
      });
    } catch {
      // Silent fail for position saves
    }
  }, [campaignId]);

  const handleSelectNode = (nodeId) => {
    setSelectedNodeId(nodeId);
  };

  // ── Create quest from narrative node ──
  const handleCreateQuestFromNode = (node) => {
    setQuestFromNodeSource(node);
    setQuestFromNodeForm({
      title: '',
      quest_type: 'side',
      summary: node.summary || '',
    });
    setQuestFromNodeOpen(true);
  };

  const submitQuestFromNode = async () => {
    if (!questFromNodeForm.title.trim() || !questFromNodeSource) return;
    setCreatingQuest(true);
    try {
      const res = await axios.post(
        `${API_URL}/narrative/nodes/${questFromNodeSource.id}/create-quest`,
        questFromNodeForm
      );
      // Add new quest to local state
      setQuests(prev => [...prev, res.data]);
      // Update node's linked_quests locally
      setNodes(prev => prev.map(n =>
        n.id === questFromNodeSource.id
          ? { ...n, linked_quests: [...(n.linked_quests || []), res.data.id] }
          : n
      ));
      toast.success(lang === 'fr' ? 'Quête créée et liée au nœud' : 'Quest created and linked to node');
      setQuestFromNodeOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setCreatingQuest(false);
    }
  };

  if (loading) {
    return (
      <MainLayout fluid>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-xs text-muted-foreground animate-pulse">
              {lang === 'fr' ? 'Chargement du fil narratif...' : 'Loading narrative thread...'}
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout fluid>
      <div className="h-[calc(100vh-4rem)] flex flex-col animate-fade-in" data-testid="narrative-timeline-page">
        {/* Main content: Graph + Details Panel */}
        <div className="flex-1 flex min-h-0">
          {/* Graph area */}
          <div className="flex-1 min-w-0 relative">
            {nodes.length === 0 ? (
              /* ── Empty state ── */
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <div className="relative mb-6">
                  <div className="w-24 h-24 rounded-2xl bg-primary/5 border border-primary/10 flex items-center justify-center">
                    <GitBranch className="h-10 w-10 text-primary/40" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/15 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-amber-500/60" />
                  </div>
                </div>
                <h2 className="font-cinzel font-bold text-xl mb-2 text-foreground">
                  {lang === 'fr' ? 'Votre fil narratif est vide' : 'Your narrative thread is empty'}
                </h2>
                <p className="text-sm text-muted-foreground mb-8 max-w-sm leading-relaxed">
                  {lang === 'fr'
                    ? 'Créez votre premier noeud narratif pour commencer à construire l\'ossature de votre scénario.'
                    : 'Create your first narrative node to start building your scenario structure.'
                  }
                </p>
                {isMJ && (
                  <button
                    onClick={handleAddNode}
                    className="group relative px-6 py-3 rounded-xl text-sm font-bold text-primary-foreground bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all hover:shadow-xl hover:shadow-primary/30 active:scale-95"
                  >
                    <Plus className="h-4 w-4 inline mr-2" />
                    {lang === 'fr' ? 'Créer le premier noeud' : 'Create First Node'}
                  </button>
                )}
              </div>
            ) : (
              <NarrativeNodeGraph
                narrativeNodes={nodes}
                narrativeEdges={edges}
                language={lang}
                isMJ={isMJ}
                selectedNodeId={selectedNodeId}
                onNodeClick={setSelectedNodeId}
                onEdgeClick={handleEdgeClick}
                onAddNode={handleAddNode}
                onConnect={handleConnect}
                onPositionsSave={handlePositionsSave}
              />
            )}
          </div>

          {/* Details panel */}
          {selectedNode && (
            <NarrativeNodeDetailsPanel
              node={selectedNode}
              edges={edges}
              allNodes={nodes}
              language={lang}
              isMJ={isMJ}
              onClose={() => setSelectedNodeId(null)}
              onEdit={handleEditNode}
              onDuplicate={handleDuplicateNode}
              onDelete={handleDeleteNode}
              onStatusChange={handleStatusChange}
              onSelectNode={handleSelectNode}
              quests={quests}
              onCreateQuestFromNode={handleCreateQuestFromNode}
            />
          )}
        </div>
      </div>

      {/* Node Editor Dialog */}
      <NarrativeNodeEditor
        open={nodeEditorOpen}
        onOpenChange={setNodeEditorOpen}
        node={editingNode}
        language={lang}
        saving={saving}
        onSave={handleSaveNode}
        npcs={npcs}
        locations={locations}
        quests={quests}
      />

      {/* Edge Editor Dialog */}
      <NarrativeEdgeEditor
        open={edgeEditorOpen}
        onOpenChange={setEdgeEditorOpen}
        edge={editingEdge}
        nodes={nodes}
        language={lang}
        saving={saving}
        onSave={handleSaveEdge}
        onDelete={handleDeleteEdge}
      />

      {/* Create Quest from Node Dialog */}
      <Dialog open={questFromNodeOpen} onOpenChange={setQuestFromNodeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cinzel flex items-center gap-2">
              <Target className="h-5 w-5 text-violet-500" />
              {lang === 'fr' ? 'Créer une quête liée' : 'Create Linked Quest'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {lang === 'fr'
                ? `Depuis le nœud : "${questFromNodeSource?.title}"`
                : `From node: "${questFromNodeSource?.title}"`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">{lang === 'fr' ? 'Titre de la quête' : 'Quest Title'} *</Label>
              <Input
                value={questFromNodeForm.title}
                onChange={(e) => setQuestFromNodeForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder={lang === 'fr' ? 'Ex: Retrouver le grimoire perdu' : 'Ex: Find the lost grimoire'}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">{lang === 'fr' ? 'Type' : 'Type'}</Label>
              <Select
                value={questFromNodeForm.quest_type}
                onValueChange={(v) => setQuestFromNodeForm(prev => ({ ...prev, quest_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">{lang === 'fr' ? 'Principale' : 'Main'}</SelectItem>
                  <SelectItem value="side">{lang === 'fr' ? 'Secondaire' : 'Side'}</SelectItem>
                  <SelectItem value="personal">{lang === 'fr' ? 'Personnelle' : 'Personal'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">{lang === 'fr' ? 'Résumé' : 'Summary'}</Label>
              <Input
                value={questFromNodeForm.summary}
                onChange={(e) => setQuestFromNodeForm(prev => ({ ...prev, summary: e.target.value }))}
                placeholder={lang === 'fr' ? 'Résumé rapide...' : 'Quick summary...'}
              />
            </div>
            {/* Show inherited data */}
            {(questFromNodeSource?.arc || questFromNodeSource?.act) && (
              <div className="text-[11px] text-muted-foreground bg-muted/40 rounded-lg p-3 space-y-1">
                <p className="font-semibold text-foreground/70">
                  {lang === 'fr' ? 'Hérité du nœud :' : 'Inherited from node:'}
                </p>
                {questFromNodeSource.arc && <p>Arc: <span className="text-foreground">{questFromNodeSource.arc}</span></p>}
                {questFromNodeSource.act && <p>{lang === 'fr' ? 'Acte' : 'Act'}: <span className="text-foreground">{questFromNodeSource.act}</span></p>}
                {questFromNodeSource.linked_npcs?.length > 0 && (
                  <p>{lang === 'fr' ? 'PNJ liés' : 'Linked NPCs'}: {questFromNodeSource.linked_npcs.length}</p>
                )}
                {questFromNodeSource.linked_locations?.length > 0 && (
                  <p>{lang === 'fr' ? 'Lieux liés' : 'Linked locations'}: {questFromNodeSource.linked_locations.length}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuestFromNodeOpen(false)} className="text-xs">
              {lang === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={submitQuestFromNode}
              disabled={creatingQuest || !questFromNodeForm.title.trim()}
              className="text-xs gap-1.5"
            >
              {creatingQuest && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {lang === 'fr' ? 'Créer la quête' : 'Create Quest'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-cinzel">
              {lang === 'fr' ? 'Confirmer la suppression' : 'Confirm Deletion'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {deleteConfirm?.type === 'node'
                ? (lang === 'fr'
                  ? 'Ce noeud et toutes ses connexions seront supprimés. Cette action est irréversible.'
                  : 'This node and all its connections will be deleted. This action cannot be undone.')
                : (lang === 'fr'
                  ? 'Cette connexion sera supprimée.'
                  : 'This connection will be deleted.')
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">{lang === 'fr' ? 'Annuler' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs"
            >
              {lang === 'fr' ? 'Supprimer' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
